import { remove } from "../../utils/lodash";

/**
 * Builds a new mention set from existing mentions.
 */
export type MentionBuilderDelegate = (mentions: MentionBuilder) => MentionBuilder;

export interface SetMention {
    value: string;
    openEnd?: string;
    customChoiceIndex?: string;
}

export class MentionBuilder {
    private _result: SetMention[] = [];

    constructor(mentions: SetMention[] = []) {
        if (mentions !== undefined) this._result = [...mentions];
    }

    /**
     * Returns an array of mentions to set.
     */
    build(): SetMention[] {
        return this._result.filter((m) => m !== undefined);
    }

    /**
     * Set a single mention, clearing all other mentions for the variable.
     */
    set(value: string, openEnd?: string): MentionBuilder {
        return this.clear().add(value, openEnd);
    }

    /**
     *  Adding values from custom questions open-ends to the existing mentions
     **/
    addOpenEndValueByCustomChoiceIndex(idx: string, openEnd?: string): MentionBuilder {
        const itemIndex = this._result.findIndex(
            ({ customChoiceIndex }) => customChoiceIndex === idx,
        );
        const item = this._result[itemIndex];
        item.openEnd = openEnd;
        this._result = [
            ...this._result.slice(0, itemIndex),
            item,
            ...this._result.slice(itemIndex + 1),
        ];
        return this;
    }

    /**
     *  Adding values from text highlight questions open-ends to the existing mentions
     **/
    addOpenEndValueByChoiceIndex(idx: string, openEnd?: string): MentionBuilder {
        const itemIndex = this._result.findIndex(({ value }) => value === idx);
        const item = this._result[itemIndex];
        item.openEnd = openEnd;
        this._result = [
            ...this._result.slice(0, itemIndex),
            item,
            ...this._result.slice(itemIndex + 1),
        ];
        return this;
    }

    /**
     * Sets a mention at the specific index.
     */
    replace(index: number, value: string, openEnd?: string): MentionBuilder {
        return this.splice(index, 1).insert(index, value, openEnd);
    }

    /**
     * Removes mentions for which predicate returns truthy.
     */
    remove(predicate: (value: string, index: number, openEnd?: string) => boolean): MentionBuilder {
        this._result = this._result.filter((v, i) => !predicate(v.value, i, v.openEnd));
        return this;
    }

    /**
     * Removes given value from mentions.
     */
    pull(value: string): MentionBuilder {
        this._result = this._result.filter((v) => v.value !== value);
        return this;
    }

    /**
     * Transforms mention values using an iterator.
     */
    mapValues(
        iterator: (value: string, index: number, openEnd?: string) => string,
    ): MentionBuilder {
        this._result = this._result.map((v, i) => ({
            ...v,
            value: iterator(v.value, i, v.openEnd),
        }));
        return this;
    }

    /**
     * Transforms mention open-ends using an iterator.
     */
    mapOpenEnds(
        iterator: (value: string, index: number, openEnd?: string) => string,
    ): MentionBuilder {
        this._result = this._result.map((v, i) => ({
            ...v,
            openEnd: iterator(v.value, i, v.openEnd),
        }));
        return this;
    }

    /**
     * Insert a mention at the specified index.
     */
    insert(index: number, value: string, openEnd?: string): MentionBuilder {
        this._result.splice(index, 0, {
            value: value,
            openEnd: openEnd,
        });
        return this;
    }

    /**
     * Adds multiple mentions.
     */
    add(mentions: SetMention[]): MentionBuilder;

    /**
     * Add a mention to the variable.
     */
    add(value: string, openEnd?: string, customChoiceIndex?: string): MentionBuilder;
    add(value?: string | SetMention[], openEnd?: string, customChoiceIndex?: string) {
        if (value == null || typeof value === "string")
            this._result.push({
                value: value as string,
                openEnd: openEnd,
                customChoiceIndex: customChoiceIndex,
            });
        else value.forEach((m) => this.add(m.value, m.openEnd, customChoiceIndex));
        return this;
    }

    /**
     * Toggles a mention of the variable, adding it if the value does not exist or removing it if the value exists.
     */
    toggle(value: string, openEnd?: string): MentionBuilder {
        return this._result.some((m) => m.value === value)
            ? this.removeValue(value)
            : this.add(value, openEnd);
    }

    /**
     * Remove all mentions with the specified value.
     */
    removeValue(value: string): MentionBuilder {
        remove(this._result, (m) => m.value === value);
        return this;
    }

    /**
     * Clear all mentions of the variable.
     */
    clear(): MentionBuilder {
        this._result = [];
        return this;
    }

    /**
     * Removes mentions starting at the specified index.
     */
    splice(start: number, count?: number): MentionBuilder {
        if (count === undefined) this._result.splice(start);
        else this._result.splice(start, count);
        return this;
    }
}
